/**
 * nb-shell-region.js
 * Owns two things for the single shared region chooser (#nb-region-sheet in
 * nb-shell-header.liquid, an nb-ui sheet): first-visit auto-open, and the variant switch
 * between its approved copy states.
 *
 * "default" (always-available, opened from either pill): heading "Region and language",
 * Singapore card first. "first-visit-my" (SG store, visitor detected in Malaysia): heading
 * "Shopping from Malaysia?", Malaysia card first, per the frozen board at
 * specs/home/shell-region/. "first-visit-sg" (MY store, visitor detected in Singapore): the
 * mirrored heading "Shopping from Singapore?", card order unchanged (Singapore already
 * sits first by default). One markup, one modal, per the 2026-09-24 audit fix.
 *
 * Whether to auto-open at all, and which direction, is decided client-side from the
 * browser's IANA timezone (`Intl.DateTimeFormat().resolvedOptions().timeZone`), 2026-09-24:
 * Asia/Kuala_Lumpur or Asia/Kuching on the SG store means "shopping from Malaysia"; Asia/
 * Singapore on the MY store means "shopping from Singapore". This is the primary signal
 * because the SG store's Markets config only knows Singapore, so Shopify's own
 * `localization.country` detection can never resolve to Malaysia for a Malaysian visitor
 * there (adding Malaysia as a market is Daniel's pricing/market call, not made here).
 *
 * nb-shell-header.liquid still computes `localization.country.iso_code` server-side and
 * writes it onto #nb-region-sheet as `data-nb-region-first-visit-variant` ("my"/"sg"/"").
 * That stays as a secondary, corroborating signal: it only matters here when the timezone
 * gives no answer, in which case a real Shopify-detected mismatch with the store's own
 * market still opens the prompt.
 *
 * A localStorage "seen" guard and a bot check both still apply, so the prompt only ever
 * interrupts once per real visitor.
 */
(function () {
  var STORAGE_KEY = 'nb-region-seen';

  function storageAvailable() {
    try {
      var testKey = '__nb_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }

  function isBot() {
    return /bot|crawl|spider|slurp|facebookexternalhit|headless/i.test(navigator.userAgent || '');
  }

  function isMyStore() {
    var host = window.location.hostname || '';
    return host.indexOf('kr4vzb') !== -1 || host.indexOf('my.nuviebaby.com') !== -1;
  }

  function timezoneDirection() {
    var tz = '';
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return '';
    }
    if (isMyStore()) {
      return tz === 'Asia/Singapore' ? 'sg' : '';
    }
    return (tz === 'Asia/Kuala_Lumpur' || tz === 'Asia/Kuching') ? 'my' : '';
  }

  function textForVariant(el, variant) {
    if (variant === 'first-visit-my') return el.getAttribute('data-first-visit-my');
    if (variant === 'first-visit-sg') return el.getAttribute('data-first-visit-sg');
    return el.getAttribute('data-default');
  }

  function applyVariant(sheet, variant) {
    if (!sheet) return;
    var heading = sheet.querySelector('[data-nb-region-heading]');
    if (heading) {
      var text = textForVariant(heading, variant);
      if (text) heading.textContent = text;
    }
    var footnote = sheet.querySelector('[data-nb-region-footnote]');
    if (footnote) {
      var footnoteText = textForVariant(footnote, variant);
      if (footnoteText) footnote.textContent = footnoteText;
    }
    var cardsWrap = sheet.querySelector('[data-nb-region-cards]');
    if (cardsWrap) {
      var sg = cardsWrap.querySelector('[data-nb-region-card="sg"]');
      var my = cardsWrap.querySelector('[data-nb-region-card="my"]');
      if (sg && my) {
        if (variant === 'first-visit-my') {
          cardsWrap.insertBefore(my, sg);
        } else {
          cardsWrap.insertBefore(sg, my);
        }
      }
    }
  }

  function wireVariantReset() {
    // Every ordinary trigger (either pill) resets the chooser to the default,
    // always-available copy and order before nb-ui's own sheet-open handler runs.
    document.querySelectorAll('[data-nb-sheet-open="nb-region-sheet"]').forEach(function (trigger) {
      if (trigger.dataset.nbRegionResetInit === 'true') return;
      trigger.dataset.nbRegionResetInit = 'true';
      trigger.addEventListener('click', function (e) {
        // Skip the synthetic click the first-visit auto-open dispatches below: only a real
        // pointer/keyboard click on the pill should reset the chooser back to its default,
        // always-available copy and order.
        if (!e.isTrusted) return;
        applyVariant(document.getElementById('nb-region-sheet'), 'default');
      });
    });
  }

  function init() {
    if (window.__nbRegionInit) return;
    window.__nbRegionInit = true;

    wireVariantReset();

    if (isBot()) return;

    var sheet = document.getElementById('nb-region-sheet');
    var serverDirection = sheet ? sheet.getAttribute('data-nb-region-first-visit-variant') : '';
    var direction = timezoneDirection() || serverDirection; // timezone is primary, Shopify's
    // own localization detection is only consulted when the timezone gives no answer
    if (direction !== 'my' && direction !== 'sg') return; // visitor's own market, or unknown

    var canStore = storageAvailable();
    var alreadySeen = false;
    if (canStore) {
      try {
        alreadySeen = !!window.localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        alreadySeen = false;
      }
    }
    if (alreadySeen) return;

    // QC-H060, 2026-09-24. First attempt: write STORAGE_KEY only after the click, and try to
    // cancel the pending timer on `pagehide`/`visibilitychange` so an old, about-to-be-
    // replaced document (a real visitor reloading, or this file's own tests doing
    // page.reload()) never fires late and steals the "first visit" for the new document.
    // That only works when the old document actually starts tearing down before the timer
    // fires. Under any real latency (a slow same-origin response, several concurrent
    // dev-server requests queued behind each other), the old document can still be fully
    // "visible" at 400ms, running to completion in exactly the window this was meant to
    // close, then the new document loads once the old one has already written "seen".
    //
    // Real fix: write STORAGE_KEY synchronously, right here, before the delay even starts.
    // A real visitor's very first pageview marks itself seen the instant the decision to
    // show is made; the 400ms delay only decides when to reveal it on THIS SAME document,
    // so the write and the click can never straddle a navigation. The one behaviour change:
    // a visitor who navigates away in under 400ms, before ever seeing the prompt, does not
    // get a second chance at it on their next page. That is a far smaller miss than showing
    // it twice, and is normal for "show once" UI generally.
    if (canStore) {
      try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
    }

    var timerId = window.setTimeout(function () {
      var trigger = document.querySelector('[data-nb-sheet-open="nb-region-sheet"]:not([hidden])') ||
        document.querySelector('[data-nb-sheet-open="nb-region-sheet"]');
      if (!trigger) return;
      applyVariant(sheet, 'first-visit-' + direction);
      trigger.click();
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
